import { decodeJSOG } from 'utils/jsonUtils';
import { addSortParameter } from 'utils/urlUtils';
import { api } from './api';

// Define a service using a base URL and expected endpoints
export const entityApi = api.injectEndpoints({
  reducerPath: 'entityApi',
  tagTypes: ['Entities', 'Contents', 'RootFolders', 'EntityMarkers', 'EntityRelationships'],
  endpoints: (builder) => ({
    findByTerm: builder.query({
      query: ({ term }) => ({
        url: `/arcade/api/entities/search?term=${term}&entityTypeCodes=DELIB`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    findByClauses: builder.query({
      query: ({ viewName, clauses, entityTypeCodes, page, size, sort }) => ({
        url: `/arcade/api/entities/search`,
        method: 'POST',
        params: { viewName, entityTypeCodes, page, size, sort },
        body: clauses
      }),
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    findBySpecification: builder.query({
      query: (params) => {
        const initialParams = {
          page: 0,
          size: 20,
          sort: 'id,desc',
          ...params
        };
        const urlParams = new URLSearchParams();
        Object.keys(initialParams).forEach((keyName) => {
          const value = initialParams[keyName];
          if (typeof value !== 'undefined') {
            if (keyName === 'sort') {
              addSortParameter(urlParams, value);
            } else {
              urlParams.append(keyName, value);
            }
          }
        });

        return {
          url: `public/api/entities/search/findBySpecification`,
          method: 'GET',
          params: urlParams
        };
      },
      transformResponse: (response, meta, { jsonView, depth }) => {
        response.decode = () => {
          if (jsonView?.includes('WithParent')) {
            return decodeJSOG(response, depth);
          }
          return response;
        };
        return response;
      },
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ id }) => ({ type: 'Entities', id }))]
          : ['Entities']
    }),
    getContent: builder.query({
      query: ({ contentId, mimeType }) => ({
        url: `arcade/api/entities/content/${contentId}`,
        method: 'GET',
        headers: {
          Accept: mimeType
        },
        responseHandler: (response) => {
          if (!response.ok) {
            return { error: response.statusText, status: response.status };
          }
          return response.blob();
        },
        cache: 'no-cache' // Enables browser cache (Etag management)
      })
    }),
    getRelatedDocuments: builder.query({
      query: ({ entityId, entityTypeCodes, resultMax = 10, scoreMin = 20, withTag = false }) => ({
        url: `arcade/api/entities/search/${entityId}/moreLikeThis`,
        method: 'GET',
        params: { entityTypeCodes, resultMax, scoreMin, withTag }
      }),
      transformResponse: (response) => {
        if (response && response.content) {
          return {
            ...response,
            content: [...response.content].sort((a, b) => b.score - a.score) // sort by score
          };
        }
        return response;
      },
      providesTags: (result) =>
        result?.content
          ? [...result.content.map(({ entity }) => ({ type: 'RelatedDocuments', id: entity.id }))]
          : ['RelatedDocuments']
    }),
    getEntityMarkers: builder.query({
      query: (id) => ({
        url: `public/api/entities/${id}/markers`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'EntityMarkers', id }]
    }),
    deleteEntityMarkers: builder.mutation({
      query: ({ id, markerCodes }) => ({
        url: `public/api/entities/${id}/markers`,
        method: 'DELETE',
        params: { markerCodes }
      }),
      providesTags: (result, error, id) => [{ type: 'EntityMarkers', id }]
    }),
    getEntityRelationships: builder.query({
      query: (id) => ({
        url: `public/api/entities/${id}/relationships`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'EntityRelationships', id }]
    }),
    publishEntity: builder.mutation({
      query: ({ id, publicationDateUpdated }) => ({
        url: `admin/api/entities/${id}/publish`,
        method: 'PUT',
        params: { publicationDateUpdated }
      }),
      invalidatesTags: (result) =>
        result ? Array.from(result).map((id) => ({ type: 'Entities', id })) : []
    }),
    publishEntities: builder.mutation({
      query: ({ ids, publicationDateUpdated }) => ({
        url: `admin/api/entities/publish`,
        method: 'PUT',
        params: { publicationDateUpdated },
        body: ids
      }),
      invalidatesTags: (result) =>
        result ? Array.from(result).map((id) => ({ type: 'Entities', id })) : []
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFindByTermQuery,
  useFindByClausesQuery,
  useFindBySpecificationQuery,
  useLazyFindBySpecificationQuery,
  useGetContentQuery,
  useGetRelatedDocumentsQuery,
  useGetEntityMarkersQuery,
  useDeleteEntityMarkersMutation,
  useGetEntityRelationshipsQuery,
  usePublishEntityMutation,
  usePublishEntitiesMutation
} = entityApi;
